import type { RequestContext } from "../context/request-context.js";
import { AppError, ErrorCode } from "../../shared/errors/app-error.js";
import { ReportService } from "./report-service.js";
import { MinutaService } from "./minuta-service.js";
import type { IntegrityReportId, InspectionId, ProfessionalId, RecommendationId } from "../../shared/kernel/identifiers.js";
import type { IntegrityReport } from "../../domain/report/integrity-report-domain.js";
import type { Recommendation } from "../../domain/recommendation/recommendation-domain.js";
import type { Minuta, MinutaCorrectionRequest } from "../../domain/report/minuta-domain.js";
import type { RecommendationRepository } from "../../repositories/operational-repositories.js";
import type { AuditRecorder } from "../../audit/audit-recorder.js";
import { AuditAction } from "../../audit/audit-log.js";
import { SignatureService } from "./signature-service.js";
import { buildMinutaDocumentModel } from "../mappers/minuta-document-mapper.js";
import { compareRecommendationsWithMinutaSnapshot } from "../mappers/minuta-document-mapper.js";
import type { UpdateRecommendationFullPayload } from "./report-service.js";
import type { AuthorizationService } from "../../permissions/authorization.js";
import { PermissionCode } from "../../permissions/permissions.js";
import { canActAsPlh } from "../../domain/identity/plh-eligibility.js";
import {
  EQUIPMENT_CONDITION_LABELS,
  EQUIPMENT_CONDITION_REQUIRED,
  RESIDUAL_LIFE_ORIGIN_LABELS,
  deriveEquipmentResult,
  isEquipmentCondition,
  normalizeEngineeringAssessment,
  residualLifeFromAvr,
  residualLifeFromEngineer,
  validateManualResidualLife,
  type EngineeringAssessment,
  type EquipmentCondition,
  type ResidualLifeAssessment
} from "../../domain/report/integrity-assessment.js";
import { residualLifeSourceOf, selectAvrForResidualLife } from "../../domain/report/avr-residual-life-source.js";
import { AVR_STATUS_LABELS, type AvrReport } from "../../domain/engineering/avr-report-domain.js";
import { sectionLabelOf, type TechnicalCorrectionItemInput, type TechnicalCorrectionRequest } from "../../domain/review/technical-correction.js";
import type { TechnicalCorrectionService } from "./technical-correction-service.js";
import type { MinutaAssessmentInfo } from "../mappers/minuta-document-mapper.js";
import { buildMinutaSource, diffMinutaSource, sourceFromMinutaModel, type MinutaSourceDiff, type MinutaSourceSnapshot } from "../../domain/report/minuta-source.js";
import type { DocumentHoldService } from "./document-hold-service.js";
import type { ProjectResponsibilityService } from "./project-responsibility-service.js";
import { RESIDUAL_LIFE_ORIGIN_LABELS as RL_LABELS } from "../../domain/report/integrity-assessment.js";
import { EQUIPMENT_CONDITION_LABELS as EC_LABELS } from "../../domain/report/integrity-assessment.js";

/** Fields expected in a Minuta revision's canonical documentary snapshot, checked before allowing "Validar Minuta" (Fase 26). */
const REQUIRED_MINUTA_DOCUMENT_FIELDS: ReadonlyArray<{ key: string; label: string }> = [
  { key: 'tag', label: 'TAG' },
  { key: 'projectName', label: 'Projeto' },
  { key: 'unitArea', label: 'Unidade / Área' },
  { key: 'fabricationStandard', label: 'Norma de Fabricação' },
  { key: 'inspectorName', label: 'Inspetor' },
  { key: 'trackingNumber', label: 'Nº da Minuta' },
  { key: 'revisionNumber', label: 'Revisão' }
];

const randomUUID = () => globalThis.crypto.randomUUID();

export interface EngineeringWorkspaceContext {
  report: IntegrityReport;
  recommendations: readonly Recommendation[];
}

export interface MinutaWorkspaceContext {
  minuta: Minuta;
  recommendations: any[];
  corrections: readonly MinutaCorrectionRequest[];
  revisions: any[];
  pendencySummary: PendencySummary;
}

export interface PendencySummary {
  totalCorrections: number;
  pending: number;
  inCorrection: number;
  corrected: number;
  awaitingValidation: number;
  validated: number;
  returned: number;
  awaitingInspector: number;
  canComplete: boolean;
  canApprove: boolean;
}

/**
 * O que o Workspace/Painel precisa saber do RI além do próprio registro:
 * cliente, projeto, unidade, TAG, tipo de equipamento, OS/Sub-OS, Inspetor
 * (com Professional) e os campos de contexto da Minuta — resolvidos pela
 * composição, nunca redigitados.
 */
export interface IntegrityReportDescription {
  clientId: string;
  client: string;
  projectId: string | null;
  project: string;
  unitArea: string;
  equipmentId: string | null;
  tag: string;
  equipmentType: string;
  osNumber: string;
  subOsNumber: string;
  inspectorProfessionalId: string | null;
  inspectorName: string;
  /** ENGENHEIRO RESPONSÁVEL do Projeto — o "Responsável" do Workspace. */
  engineerProfessionalId: string | null;
  engineerName: string;
  plhProfessionalId: string | null;
  plhName: string;
  inspectionDate: string;
  projectCode: string;
  fabricationCode: string;
  fabricationStandard: string;
  /** SPEC / especificação de padronização de material do Equipamento (`Equipment.spec`) — além da norma de fabricação, para o cabeçalho fixo da Minuta (Melhoria 012). */
  materialSpec: string;
  /** Sistema de Tubulação do Equipamento (`Equipment.system`) — vazio quando não aplicável (mesma regra da Melhoria 002). */
  systemName: string;
}

export interface IntegrityEngineeringLookups {
  describe(context: RequestContext, report: IntegrityReport): Promise<IntegrityReportDescription>;
  /** AVRs do equipamento (mesma TAG/contexto) — a escolha do válido é regra de domínio. */
  listAvrs(context: RequestContext, clientId: string, equipmentId: string | null): Promise<readonly AvrReport[]>;
  /** Relatórios END do contexto (Cliente/Projeto/Unidade/Equipamento) enviados à Engenharia — aba END do Workspace. */
  listNdtForContext?(context: RequestContext, scope: { clientId: string; projectId: string | null; unitAreaId: string | null; equipmentId: string | null; equipmentTag: string }): Promise<EngineeringNdtRow[]>;
}

/** Linha da aba END (Ensaios) do Workspace do RI. */
export interface EngineeringNdtRow {
  documentType: string;
  typeLabel: string;
  documentId: string;
  documentNumber: string;
  revision: string;
  tag: string;
  testDate: string;
  inspectorName: string;
  status: string;
  statusLabel: string;
  submittedAt: string | null;
  reviewRoute: string | null;
  documentRoute: string;
}

/** AVR encontrada para a TAG — separada em "para Engenharia" (qualquer estado) e "válida para Vida Residual". */
export interface RelatedAvrRow {
  avrId: string;
  avrNumber: string;
  revision: string;
  status: string;
  statusLabel: string;
  validForResidualLife: boolean;
  route: string;
  reviewRoute: string;
}

export interface MinutaValidationError {
  code: 'MISSING_ZR' | 'MINUTA_NOT_FOUND' | 'INVALID_STATUS' | string;
  recommendationId?: string;
  recommendationNumber?: string;
  message: string;
}

export interface MinutaTecnicaContext {
  minuta: Minuta | null;
  /** Fonte atual (hash) calculada do relatório vivo. */
  source: MinutaSourceSnapshot;
  diff: MinutaSourceDiff;
  updateNeeded: boolean;
  workspace: MinutaWorkspaceContext | null;
  /** Pendências que impedem VALIDAR MINUTA (vazio = pode validar). */
  validationReasons: string[];
  /** Erros impeditivos estruturados. */
  errors?: MinutaValidationError[];
  /** Avisos não-impeditivos. */
  warnings?: string[];
}

export interface FinalizationGate {
  allowed: boolean;
  reasons: string[];
  /** Rótulo do estado após finalizar ("FINALIZADO" / "FINALIZADO — AGUARDANDO ASSINATURA"). */
  resultLabel: string;
}

export interface EngineeringAssessmentContext {
  report: IntegrityReport;
  description: IntegrityReportDescription;
  assessment: EngineeringAssessment;
  /** AVR válido encontrado para a TAG — quando existe, a Vida Residual vem dele (campo manual desabilitado). */
  avr: { found: true; avrId: string; avrNumber: string; revision: string; date: string; remainingLifeYears: number; calculatedMonths: number | null; adoptedMonths: number | null; capped: boolean } | { found: false };
  /** Todas as AVRs relacionadas (inclusive em elaboração) — nunca se afirma "nenhum AVR" quando existe uma em andamento. */
  relatedAvrs: RelatedAvrRow[];
  minuta: Minuta | null;
  /** P0-02 V4: todas as Minutas da inspeção (PARADA e/ou ROTINA). Campo opcional para compatibilidade retroativa. */
  minutas?: readonly Minuta[];
}

export class EngineeringService {
  private lookups: IntegrityEngineeringLookups | null = null;
  /**
   * STUDIO INSPECT IA: logo documental PUBLICADO no momento em que a revisão
   * da Minuta é criada. Vai para dentro do snapshot da revisão — que nunca é
   * reescrito — e por isso a Minuta impressa depois continua com o logo da
   * época. Sem a ligação: null = texto INSPECT IA de sempre.
   */
  private documentLogo: (() => Promise<string | null>) | null = null;

  attachDocumentLogo(provider: () => Promise<string | null>): void {
    this.documentLogo = provider;
  }

  private async minutaLogo(): Promise<string | null> {
    return this.documentLogo ? await this.documentLogo().catch(() => null) : null;
  }
  private corrections: TechnicalCorrectionService | null = null;
  private holds: DocumentHoldService | null = null;
  private responsibility: ProjectResponsibilityService | null = null;

  /** Composição do Painel de Engenharia: descrições do RI, AVRs, correções por item, HOLD e responsáveis do Projeto. */
  attachPanelServices(input: { lookups?: IntegrityEngineeringLookups; corrections?: TechnicalCorrectionService; holds?: DocumentHoldService; responsibility?: ProjectResponsibilityService }): void {
    if (input.lookups) this.lookups = input.lookups;
    if (input.corrections) this.corrections = input.corrections;
    if (input.holds) this.holds = input.holds;
    if (input.responsibility) this.responsibility = input.responsibility;
  }

  /**
   * CLIENTE DO DOCUMENTO — base canônica de qualquer autorização documental.
   *
   * A Minuta não guarda `clientId`: guarda o Projeto, e o Projeto é de um
   * Cliente. A resolução usa o mesmo serviço central que RI, ME, AVR e o
   * ciclo de revisão usam (`ProjectResponsibilityService`), para não existir
   * uma segunda maneira de responder "de quem é este documento".
   *
   * Não resolveu, não autoriza. `context.activeClientId` NÃO é usado como
   * alternativa: cair nele silenciosamente é exatamente o defeito que esta
   * correção remove — a autorização passaria a depender do que está
   * selecionado na tela.
   */
  /** PLH RESPONSÁVEL nomeado por um Cliente — o que o gate compara. */
  private async responsiblePlhOfClient(clientId: string): Promise<string | null> {
    const identityRepo = this.reportService.identityRepo as any;
    if (!identityRepo?.getClient) return null;
    const client = await identityRepo.getClient(clientId).catch(() => null);
    return client?.responsiblePlhProfessionalId ? String(client.responsiblePlhProfessionalId) : null;
  }

  private async clientOfMinuta(minuta: { id: string; projectId?: string | null }): Promise<string> {
    const projectId = minuta.projectId ? String(minuta.projectId) : "";
    const project = projectId && this.responsibility ? await this.responsibility.getProject(projectId) : null;
    const clientId = project?.clientId ? String(project.clientId) : "";
    if (!clientId) {
      throw new AppError(ErrorCode.Validation, `Minuta ${minuta.id} has no resolvable client`, {
        safeMessage: "CLIENTE DO DOCUMENTO NÃO IDENTIFICADO. Não é possível autorizar a assinatura sem saber a que Cliente este documento pertence."
      });
    }
    return clientId;
  }

  constructor(
    private reportService: ReportService,
    private signatureService: SignatureService,
    private auth: AuthorizationService,
    private minutaService?: MinutaService,
    private recommendationRepo?: RecommendationRepository,
    private audit?: AuditRecorder
  ) {}

  async getWorkspaceContext(
    context: RequestContext,
    reportId: IntegrityReportId
  ): Promise<EngineeringWorkspaceContext> {

    const report = await this.reportService.getReportById(context, reportId);
    if (!report) {
      throw new AppError(ErrorCode.NotFound, "Relatório não encontrado.");
    }

    const recommendations = await this.reportService.listRecommendationsByReport(context, reportId);

    return {
      report,
      recommendations,
    };
  }

  async startAnalysis(
    context: RequestContext,
    reportId: IntegrityReportId
  ): Promise<IntegrityReport> {
    this.auth.require(context, PermissionCode.EngineeringStartAnalysis);
    const report = await this.reportService.getReportById(context, reportId);
    if (!report) {
      throw new AppError(ErrorCode.NotFound, "Relatório não encontrado.");
    }

    if (report.status !== 'SUBMITTED_TO_ENGINEERING' && (report.status as any) !== 'RESUBMITTED') {
      if (report.status === 'UNDER_ENGINEERING_REVIEW') {
        await this.syncMinutaAnalysisWithReport(context, report);
        return report;
      }
      throw new AppError(ErrorCode.Conflict, "O relatório não está apto para iniciar análise.");
    }

    report.status = 'UNDER_ENGINEERING_REVIEW';
    report.updatedAt = new Date().toISOString();
    report.version++;
    await this.reportService.reportRepo.save(context, report);
    await this.syncMinutaAnalysisWithReport(context, report);
    return report;
  }

  // P0-02 V4 Etapa 5: sincronizar análise em TODAS as Minutas da inspeção (PARADA e ROTINA).
  private async syncMinutaAnalysisWithReport(context: RequestContext, report: IntegrityReport): Promise<void> {
    if (!this.minutaService) return;
    try {
      const minutas = await this.minutaService.listMinutasByInspection(context, String(report.inspectionId));
      for (const minuta of minutas) {
        if (minuta.status === 'EMITIDA' || minuta.status === 'AGUARDANDO ANÁLISE' || minuta.status === 'CORREÇÃO NECESSÁRIA') {
          await this.startMinutaAnalysis(context, minuta.id);
        }
      }
    } catch (e) {
      console.warn("[MINUTA-SYNC] Could not sync minuta analysis with report:", e);
    }
  }

  async returnForCorrection(
    context: RequestContext,
    reportId: IntegrityReportId,
    reason: string,
    comments: string
  ): Promise<IntegrityReport> {
    const fullComment = `Motivo: ${reason}\nDetalhes: ${comments}`;
    return this.reportService.returnByPlh(context, reportId, fullComment);
  }

  /** "Editar Relatório" (Bloco 9) — RI-exclusive fields only; UPDATE, never a parallel record. */
  async editReportFields(
    context: RequestContext,
    reportId: IntegrityReportId,
    payload: Parameters<ReportService['editReportFields']>[2]
  ): Promise<IntegrityReport> {
    return this.reportService.editReportFields(context, reportId, payload);
  }

  /** "Validar Relatório" (Bloco 9) — Engineering declares the RI conforme; not the final PLH signature. */
  async validateReport(
    context: RequestContext,
    reportId: IntegrityReportId
  ): Promise<IntegrityReport> {
    return this.reportService.validateReport(context, reportId);
  }

  async approveTechnical(
    context: RequestContext,
    reportId: IntegrityReportId,
    userEmail: string
  ): Promise<IntegrityReport> {
    const report = await this.reportService.approveByPlh(context, reportId);
    
    if (report.engineerPlhId && userEmail) {
      await this.signatureService.sendSignatureRequestEmail(
        context,
        report.id,
        report.engineerPlhId,
        userEmail
      );
    }
    
    return report;
  }

  async reopenForReview(
    context: RequestContext,
    reportId: IntegrityReportId
  ): Promise<IntegrityReport> {
    return this.reportService.reopenForReview(context, reportId);
  }

  // ============================================================
  // PAINEL DE ENGENHARIA — AVALIAÇÃO (CONDIÇÃO + VIDA RESIDUAL), MINUTA, CORREÇÃO POR ITEM
  // ============================================================

  /**
   * Descrição do RI. RESPONSÁVEL = Engenheiro do Projeto; PLH = PLH do
   * Projeto (fonte de verdade). Depois do envio ao PLH prevalece o snapshot
   * congelado no RI (responsibility) — o Projeto pode mudar, a revisão não.
   */
  private async describeReport(context: RequestContext, report: IntegrityReport): Promise<IntegrityReportDescription> {
    const base: IntegrityReportDescription = this.lookups
      ? await this.lookups.describe(context, report)
      : { clientId: String(report.clientId), client: String(report.clientId), projectId: null, project: "", unitArea: "", equipmentId: null, tag: "", equipmentType: "", osNumber: "", subOsNumber: "", inspectorProfessionalId: report.inspectorId ? String(report.inspectorId) : null, inspectorName: "", engineerProfessionalId: null, engineerName: "", plhProfessionalId: null, plhName: "", inspectionDate: "", projectCode: "", fabricationCode: "", fabricationStandard: "", materialSpec: "", systemName: "" };
    const frozen = report.responsibility;
    const submitted = !["DRAFT", "IN_ELABORATION", "FINALIZED_BY_INSPECTOR", "RETURNED", "IN_CORRECTION"].includes(report.status);
    if (frozen && submitted) {
      return { ...base, engineerProfessionalId: frozen.engineerProfessionalId, engineerName: frozen.engineerName, plhProfessionalId: frozen.plhProfessionalId, plhName: frozen.plhName };
    }
    if (this.responsibility && base.projectId) {
      const [eng, plh] = await Promise.all([this.responsibility.resolveProjectEngineer(context, base.projectId), this.responsibility.resolveProjectPlh(context, base.projectId)]);
      return { ...base, engineerProfessionalId: eng.responsible?.professionalId ?? null, engineerName: eng.responsible?.name ?? "", plhProfessionalId: plh.responsible?.professionalId ?? null, plhName: plh.responsible?.name ?? base.plhName };
    }
    return base;
  }

  /** CONSULTA 1 — AVRs relacionadas à TAG/equipamento/projeto em qualquer estado (Engenharia acompanha). */
  async findRelatedAvrsForEngineering(context: RequestContext, report: IntegrityReport, description?: IntegrityReportDescription): Promise<AvrReport[]> {
    if (!this.lookups) return [];
    const d = description ?? await this.describeReport(context, report);
    const avrs = await this.lookups.listAvrs(context, String(report.clientId), d.equipmentId).catch(() => [] as readonly AvrReport[]);
    return avrs.filter(a => !d.projectId || !a.projectId || a.projectId === d.projectId);
  }

  /** CONSULTA 2 — só AVR tecnicamente válida/aprovada alimenta a Vida Residual. */
  async findValidAvrForResidualLife(context: RequestContext, report: IntegrityReport, description?: IntegrityReportDescription): Promise<AvrReport | null> {
    const d = description ?? await this.describeReport(context, report);
    return selectAvrForResidualLife(await this.findRelatedAvrsForEngineering(context, report, d), d.equipmentId);
  }

  /** @deprecated use findValidAvrForResidualLife — mantido para chamadas antigas. */
  async findAvrForReport(context: RequestContext, report: IntegrityReport, description?: IntegrityReportDescription): Promise<AvrReport | null> {
    return this.findValidAvrForResidualLife(context, report, description);
  }

  private relatedAvrRows(avrs: readonly AvrReport[], valid: AvrReport | null): RelatedAvrRow[] {
    return avrs.map(a => ({ avrId: a.id, avrNumber: a.avrNumber, revision: a.revision, status: a.status, statusLabel: AVR_STATUS_LABELS[a.status] || a.status, validForResidualLife: !!valid && valid.id === a.id, route: `/integrity/avr/${a.id}`, reviewRoute: `/engineering/avr/${a.id}` }));
  }

  /** Contexto da análise: cabeçalho macro + avaliação atual + AVR encontrado + Minuta da inspeção. */
  async getAssessmentContext(context: RequestContext, reportId: IntegrityReportId): Promise<EngineeringAssessmentContext> {
    const report = await this.reportService.getReportById(context, reportId);
    if (!report) throw new AppError(ErrorCode.NotFound, "Relatório não encontrado.");
    const description = await this.describeReport(context, report);
    const assessment = normalizeEngineeringAssessment(report.engineeringAssessment);
    const related = await this.findRelatedAvrsForEngineering(context, report, description);
    const avr = selectAvrForResidualLife(related, description.equipmentId);
    const source = avr ? residualLifeSourceOf(avr) : null;
    const preview = source ? residualLifeFromAvr(source, new Date().toISOString(), context.userId || "system") : null;
    // P0-02 V4 Etapa 6: retornar todas as Minutas da inspeção (PARADA e/ou ROTINA).
    // Compatibilidade: campo `minuta` recebe a primeira existente; callers devem migrar para `minutas`.
    const minutas = this.minutaService ? await this.minutaService.listMinutasByInspection(context, String(report.inspectionId)).catch(() => [] as Minuta[]) : [];
    const minuta = minutas.length > 0 ? minutas[0] : null;
    return {
      report,
      description,
      assessment,
      avr: source && preview
        ? { found: true, avrId: source.avrId, avrNumber: source.avrNumber, revision: source.revision, date: source.date, remainingLifeYears: source.remainingLifeYears, calculatedMonths: preview.calculatedMonths, adoptedMonths: preview.adoptedMonths, capped: preview.capped }
        : { found: false },
      relatedAvrs: this.relatedAvrRows(related, avr),
      // P0-02 V4: compatibilidade — campo singular recebe a primeira Minuta ou null (não undefined).
      minuta: minuta ?? null,
      minutas
    };
  }

  /**
   * SALVAR AVALIAÇÃO: condição obrigatória (resultado derivado); Vida
   * Residual do AVR quando existe (o valor manual é ignorado — o campo fica
   * desabilitado), senão informada pelo Engenheiro (1 a 240 meses).
   */
  async saveAssessment(context: RequestContext, reportId: IntegrityReportId, input: { equipmentCondition: EquipmentCondition | string | null; residualLifeMonths?: number | string | null }): Promise<EngineeringAssessmentContext> {
    const report = await this.reportService.getReportById(context, reportId);
    if (!report) throw new AppError(ErrorCode.NotFound, "Relatório não encontrado.");
    if (!isEquipmentCondition(input.equipmentCondition)) {
      throw new AppError(ErrorCode.Validation, "Condition required", { safeMessage: EQUIPMENT_CONDITION_REQUIRED });
    }
    const now = new Date().toISOString();
    const by = context.userId || "system";
    const description = await this.describeReport(context, report);
    const avr = await this.findValidAvrForResidualLife(context, report, description);
    const source = avr ? residualLifeSourceOf(avr) : null;
    let residualLife: ResidualLifeAssessment | null;
    if (source) {
      residualLife = residualLifeFromAvr(source, now, by);
    } else if (input.residualLifeMonths === undefined || input.residualLifeMonths === null || input.residualLifeMonths === "") {
      residualLife = report.engineeringAssessment?.residualLife?.origin === "ENGINEER" ? report.engineeringAssessment.residualLife : null;
    } else {
      const errors = validateManualResidualLife(input.residualLifeMonths);
      if (errors.length > 0) throw new AppError(ErrorCode.Validation, "Residual life out of range", { safeMessage: errors[0]! });
      residualLife = residualLifeFromEngineer(input.residualLifeMonths, now, by);
    }
    const assessment: EngineeringAssessment = {
      equipmentCondition: input.equipmentCondition,
      equipmentResult: deriveEquipmentResult(input.equipmentCondition),
      residualLife,
      updatedAt: now,
      updatedBy: by
    };
    await this.reportService.setEngineeringAssessment(context, reportId, assessment);
    return this.getAssessmentContext(context, reportId);
  }

  /** Bloco "Avaliação de Engenharia" da Minuta — dados-fonte do RI (nada é redigitado). */
  minutaAssessmentOf(assessment: EngineeringAssessment | null | undefined): MinutaAssessmentInfo | undefined {
    const a = normalizeEngineeringAssessment(assessment);
    if (!a.equipmentCondition && !a.residualLife) return undefined;
    const life = a.residualLife;
    return {
      equipmentCondition: a.equipmentCondition ? EQUIPMENT_CONDITION_LABELS[a.equipmentCondition] : "—",
      equipmentResult: a.equipmentResult || "—",
      residualLifeAdoptedMonths: life ? life.adoptedMonths : null,
      residualLifeCalculatedMonths: life ? life.calculatedMonths : null,
      residualLifeOrigin: life ? RESIDUAL_LIFE_ORIGIN_LABELS[life.origin] : "",
      residualLifeSource: life?.source ? `${life.source.avrNumber} / Rev. ${life.source.revision}${life.source.date ? ` / ${life.source.date.split("-").reverse().join("/")}` : ""}` : ""
    };
  }

  /**
   * GERAR MINUTA a partir do RI em análise: Cliente, Projeto, TAG, OS,
   * Sub-OS, Inspetor, Recomendações, Condição e Vida Residual saem dos dados
   * já existentes. Se a Minuta da inspeção já existe, ela é devolvida — nunca
   * se cria uma segunda.
   */
  /**
   * P0-02: Gera ou recupera Minutas segregadas por executionType (PARADA/ROTINA).
   * Retorna a primeira Minuta encontrada (compatibilidade com callers antigos) mas
   * cria/atualiza todos os tipos presentes nas recomendações.
   */
  async generateMinutaFromReport(context: RequestContext, reportId: IntegrityReportId): Promise<{ minuta: Minuta; created: boolean }> {
    this.auth.require(context, PermissionCode.EngineeringMinuteCreate);
    if (!this.minutaService) throw new AppError(ErrorCode.NotFound, "MinutaService not available");
    const report = await this.reportService.getReportById(context, reportId);
    if (!report) throw new AppError(ErrorCode.NotFound, "Relatório não encontrado.");
    const description = await this.describeReport(context, report);
    const recommendations = await this.reportService.listRecommendationsByReport(context, reportId);

    // P0-02: agrupar recomendações por executionType normalizado.
    const normalizeExec = (r: any): 'PARADA' | 'ROTINA' => {
      const raw = (r.executeIn || r.executionType || r.execution || '').toString().toUpperCase();
      if (raw === 'SHUTDOWN' || raw === 'PARADA') return 'PARADA';
      return 'ROTINA';
    };
    // P0-02: agrupar em arrays mutáveis (recommendations é readonly).
    const paradaRecs: Recommendation[] = [];
    const rotinaRecs: Recommendation[] = [];
    for (const rec of recommendations) {
      const t = normalizeExec(rec);
      if (t === 'PARADA') paradaRecs.push(rec);
      else rotinaRecs.push(rec);
    }
    const byType = new Map<'PARADA' | 'ROTINA', Recommendation[]>();
    if (paradaRecs.length > 0) byType.set('PARADA', paradaRecs);
    if (rotinaRecs.length > 0) byType.set('ROTINA', rotinaRecs);

    let firstResult: { minuta: Minuta; created: boolean } | null = null;

    for (const [execType, recs] of byType) {
      const existing = await this.minutaService.getMinutaByInspectionAndExecutionType(context, String(report.inspectionId), execType);
      if (existing) {
        if (!firstResult) firstResult = { minuta: existing, created: false };
        continue;
      }
      const contextData = {
        clientName: description.client || String(report.clientId),
        projectName: description.project || "N/A",
        unitArea: description.unitArea || "N/A",
        osNumber: [description.osNumber, description.subOsNumber].filter(Boolean).join(" / ") || "N/A",
        tag: description.tag || "N/A",
        equipmentType: description.equipmentType || "EQUIPAMENTO",
        projectCode: description.projectCode || "N/A",
        fabricationCode: description.fabricationCode || "N/A",
        fabricationStandard: description.fabricationStandard || "N/A",
        materialSpec: description.materialSpec || "N/A",
        systemName: description.systemName || "N/A",
        inspectorName: description.inspectorName || "Inspetor Responsável",
        tenantLogo: await this.minutaLogo(),
        ...(this.minutaAssessmentOf(report.engineeringAssessment) ? { assessment: this.minutaAssessmentOf(report.engineeringAssessment)! } : {})
      };
      const model = buildMinutaDocumentModel(contextData, [...recs]);
      const source = this.minutaSourceOf(report, description, [...recs]);
      const result = await this.minutaService.createInitialMinuta(
        context,
        description.equipmentId || String(report.equipmentSnapshotId || ""),
        String(report.inspectionId),
        description.projectId || description.projectCode || "PRJ",
        model,
        source,
        execType
      );

      if (['UNDER_ENGINEERING_REVIEW', 'APPROVED', 'AWAITING_SIGNATURE', 'SIGNED', 'COMPLETED'].includes(report.status)) {
        try {
          const started = await this.startMinutaAnalysis(context, result.minuta.id);
          result.minuta = started;
        } catch (e) {
          console.warn(`[MINUTA-AUTO-START] Could not auto-start ${execType} minuta analysis:`, e);
        }
      }

      if (this.audit) {
        await this.audit.record(context, {
          action: AuditAction.MinuteCreated,
          entityType: "MINUTA",
          entityId: result.minuta.id,
          previousState: null,
          newState: { trackingNumber: result.minuta.trackingNumber, revision: result.minuta.currentRevision, executionType: execType } as any,
          metadata: { reportId: String(report.id), reportNumber: report.reportNumber ?? null, inspectionId: String(report.inspectionId), executionType: execType, recommendations: recs.length, condition: report.engineeringAssessment?.equipmentCondition ?? null, residualLifeMonths: report.engineeringAssessment?.residualLife?.adoptedMonths ?? null, performedBy: context.userId || "system" }
        });
      }
      if (!firstResult) firstResult = { minuta: result.minuta, created: true };
    }

    if (!firstResult) {
      throw new AppError(ErrorCode.Validation, "Nenhuma recomendação elegível para gerar Minuta (PARADA ou ROTINA).");
    }
    return firstResult;
  }

  /** Aba END (Ensaios) do Workspace: relatórios END do contexto do RI com o estado na Engenharia. */
  async listNdtForReport(context: RequestContext, reportId: IntegrityReportId): Promise<EngineeringNdtRow[]> {
    if (!this.lookups?.listNdtForContext) return [];
    const report = await this.reportService.getReportById(context, reportId);
    if (!report) throw new AppError(ErrorCode.NotFound, "Relatório não encontrado.");
    const d = await this.describeReport(context, report);
    return this.lookups.listNdtForContext(context, { clientId: String(report.clientId), projectId: d.projectId, unitAreaId: null, equipmentId: d.equipmentId, equipmentTag: d.tag });
  }

  // ============================================================
  // MINUTA TÉCNICA — fonte única, hash da fonte, atualização, validação, correção
  // ============================================================

  /** Fonte (hash) do que realmente entra na Minuta deste RI. */
  minutaSourceOf(report: IntegrityReport, description: IntegrityReportDescription, recommendations: any[]): MinutaSourceSnapshot {
    const a = normalizeEngineeringAssessment(report.engineeringAssessment);
    return buildMinutaSource({
      reportId: String(report.id),
      reportRevision: report.revision || "00",
      recommendations,
      assessment: a.equipmentCondition || a.residualLife ? { equipmentCondition: a.equipmentCondition ? EC_LABELS[a.equipmentCondition] : "", equipmentResult: a.equipmentResult || "", residualLifeMonths: a.residualLife ? a.residualLife.adoptedMonths : null, residualLifeOrigin: a.residualLife ? RL_LABELS[a.residualLife.origin] : "" } : null,
      documentary: { clientName: description.client || String(report.clientId), projectName: description.project || "N/A", unitArea: description.unitArea || "N/A", osNumber: [description.osNumber, description.subOsNumber].filter(Boolean).join(" / ") || "N/A", tag: description.tag || "N/A", equipmentType: description.equipmentType || "EQUIPAMENTO", fabricationStandard: description.fabricationStandard || "N/A", inspectorName: description.inspectorName || "Inspetor Responsável" }
    });
  }

  /** Fonte da revisão ATUAL da Minuta: o hash gravado ou, para Minutas anteriores ao hash, reconstruído do modelo (e gravado). */
  private async storedMinutaSource(context: RequestContext, minuta: Minuta, report: IntegrityReport): Promise<MinutaSourceSnapshot | null> {
    if (minuta.lastSourceSnapshot && minuta.lastSourceHash) return minuta.lastSourceSnapshot;
    if (!this.minutaService) return null;
    const revisions = await this.minutaService.getRevisions(context, minuta.id);
    const current = revisions.find(r => r.status === "ATUAL") ?? revisions[revisions.length - 1];
    if (!current?.snapshotData) return null;
    const model = current.snapshotData.data || current.snapshotData;
    const source = sourceFromMinutaModel(model, String(report.id), report.revision || "00", new Date(current.createdAt).toISOString());
    await this.minutaService.saveSource(context, minuta.id, source);
    return source;
  }

  /** Contexto da MINUTA TÉCNICA do RI: minuta segregada por executionType, diff pela fonte (hash), pendências de validação. */
  async getMinutaTecnicaContext(context: RequestContext, reportId: IntegrityReportId, executionType?: 'PARADA' | 'ROTINA'): Promise<MinutaTecnicaContext> {
    if (!this.minutaService) throw new AppError(ErrorCode.NotFound, "MinutaService not available");
    const report = await this.reportService.getReportById(context, reportId);
    if (!report) throw new AppError(ErrorCode.NotFound, "Relatório não encontrado.");
    // P0-02 V4 Etapa 4: executionType é obrigatório para contexto segregado.
    // Se não fornecido, tentar inferir da primeira Minuta existente (compatibilidade), mas logar aviso.
    let effectiveExecType = executionType;
    if (!effectiveExecType) {
      const allMinutas = await this.minutaService.listMinutasByInspection(context, String(report.inspectionId));
      if (allMinutas.length === 1 && allMinutas[0]) {
        effectiveExecType = allMinutas[0].executionType;
        console.warn("[P0-02-V4] getMinutaTecnicaContext chamado sem executionType; inferido da única Minuta existente:", effectiveExecType);
      } else if (allMinutas.length > 1) {
        throw new AppError(ErrorCode.Validation, "executionType obrigatório quando existem múltiplas Minutas (PARADA/ROTINA). Especifique qual documento consultar.");
      } else {
        // Nenhuma minuta existe — retornar contexto vazio com erro claro
        const emptySource = this.minutaSourceOf(report, await this.describeReport(context, report), []);
        return { minuta: null, source: emptySource, diff: diffMinutaSource(null, emptySource), updateNeeded: false, workspace: null, validationReasons: ["a Minuta ainda não foi gerada"], errors: [{ code: "MINUTA_NOT_FOUND", message: "a Minuta ainda não foi gerada" }], warnings: [] };
      }
    }
    const description = await this.describeReport(context, report);
    // P0-02 V4 Etapa 4: filtrar recomendações pelo mesmo executionType da Minuta.
    const allRecommendations = [...await this.reportService.listRecommendationsByReport(context, reportId)];
    const normalizeExec = (r: any): 'PARADA' | 'ROTINA' => {
      const raw = (r.executeIn || r.executionType || r.execution || '').toString().toUpperCase();
      if (raw === 'SHUTDOWN' || raw === 'PARADA') return 'PARADA';
      return 'ROTINA';
    };
    const recommendations = allRecommendations.filter(r => normalizeExec(r) === effectiveExecType);
    const source = this.minutaSourceOf(report, description, recommendations);
    let minuta = await this.minutaService.getMinutaByInspectionAndExecutionType(context, String(report.inspectionId), effectiveExecType);
    if (!minuta) return { minuta: null, source, diff: diffMinutaSource(null, source), updateNeeded: false, workspace: null, validationReasons: [`a Minuta ${effectiveExecType} ainda não foi gerada`], errors: [{ code: "MINUTA_NOT_FOUND", message: `a Minuta ${effectiveExecType} ainda não foi gerada` }], warnings: [] };

    // Auto-sync Minuta to EM ANÁLISE when report is under engineering review
    if (['UNDER_ENGINEERING_REVIEW', 'APPROVED', 'AWAITING_SIGNATURE', 'SIGNED', 'COMPLETED'].includes(report.status)) {
      if (minuta.status === 'EMITIDA' || minuta.status === 'AGUARDANDO ANÁLISE') {
        try {
          minuta = await this.startMinutaAnalysis(context, minuta.id);
        } catch (e) {
          console.warn("[MINUTA-AUTO-START] Could not transition minuta to EM ANÁLISE:", e);
        }
      }
    }

    const previous = await this.storedMinutaSource(context, minuta, report);
    const diff = diffMinutaSource(previous, source);
    const workspace = await this.getMinutaWorkspaceContext(context, minuta.id);
    const validation = await this.minutaValidationReasons(context, report, description, workspace, diff);
    return { minuta: workspace.minuta, source, diff, updateNeeded: diff.updateNeeded, workspace, validationReasons: validation.reasons, errors: validation.errors, warnings: validation.warnings };
  }

  /**
   * O que VALIDAR MINUTA confere (Regra oficial):
   * ÚNICO BLOQUEIO = Recomendação existente sem Nº ZR.
   * Condição do Equipamento ausente, Vida Residual ausente e 0 Recomendações são apenas AVISOS (warnings) e NÃO BLOQUEIAM.
   */
  private async minutaValidationReasons(
    context: RequestContext,
    report: IntegrityReport,
    description: IntegrityReportDescription,
    workspace: MinutaWorkspaceContext,
    diff: MinutaSourceDiff
  ): Promise<{ errors: MinutaValidationError[]; warnings: string[]; reasons: string[] }> {
    const errors: MinutaValidationError[] = [];
    const warnings: string[] = [];

    // 1. Recomendações: ÚNICO bloqueio é Recomendação existente sem Nº ZR
    const recs: readonly any[] = workspace.recommendations && workspace.recommendations.length > 0
      ? workspace.recommendations
      : (await this.reportService.listRecommendationsByReport(context, report.id));

    if (recs.length === 0) {
      warnings.push("A Minuta não possui Recomendações registradas.");
    } else {
      const missingZr = recs.filter((r: any) => {
        const zrVal = r.zr ?? r.zrNumber ?? r.zoneRecommendationNumber;
        return zrVal === null || zrVal === undefined || String(zrVal).trim().length === 0 || String(zrVal).trim() === "undefined" || String(zrVal).trim() === "null";
      });
      if (missingZr.length > 0) {
        for (const r of missingZr) {
          const code = r.recommendationNumber || r.code || r.id || "REC";
          errors.push({
            code: "MISSING_ZR",
            recommendationId: String(r.id),
            recommendationNumber: code,
            message: `A recomendação ${code} não possui Nº ZR informado.`
          });
        }
      }
    }

    // 2. Avisos informativos (NÃO IMPEDEM VALIDAÇÃO)
    const a = normalizeEngineeringAssessment(report.engineeringAssessment);
    if (!a.equipmentCondition) {
      warnings.push("Condição do Equipamento não definida");
    }
    if (!a.residualLife) {
      warnings.push("Vida Residual não definida");
    }

    // 3. Atualização pendente
    if (diff.updateNeeded) {
      errors.push({
        code: "UPDATE_NEEDED",
        message: `existem ${diff.pendingCount} alteração(ões) de fonte não incorporada(s) — gere a atualização da Minuta`
      });
    }

    // 4. Status da Minuta
    const minuta = workspace.minuta;
    if (!["EM ANÁLISE"].includes(minuta.status)) {
      errors.push({
        code: "INVALID_STATUS",
        message: minuta.status === "CONFORME" || minuta.status === "ANÁLISE CONCLUÍDA" ? "a Minuta já está validada" : minuta.status === "APROVADA" ? "a Minuta já está aprovada" : "inicie a análise da Minuta"
      });
    }

    const reasons = errors.map(e => e.message);
    return { errors, warnings, reasons };
  }

  /** GERAR ATUALIZAÇÃO DA MINUTA: nova revisão com a fonte atual; depois disso NOVAS = ALTERADAS = PENDENTES = 0. */
  async updateMinutaFromReport(context: RequestContext, reportId: IntegrityReportId, reason = "Atualização das Recomendações/dados-fonte", executionType?: 'PARADA' | 'ROTINA'): Promise<MinutaTecnicaContext> {
    this.auth.require(context, PermissionCode.EngineeringMinuteEdit);
    if (!this.minutaService) throw new AppError(ErrorCode.NotFound, "MinutaService not available");
    const report = await this.reportService.getReportById(context, reportId);
    if (!report) throw new AppError(ErrorCode.NotFound, "Relatório não encontrado.");
    // P0-02 V4 Etapa 3: executionType é obrigatório para atualização segregada.
    // Se não fornecido, tentar inferir da primeira Minuta existente (compatibilidade), mas logar aviso.
    let effectiveExecType = executionType;
    if (!effectiveExecType) {
      const allMinutas = await this.minutaService.listMinutasByInspection(context, String(report.inspectionId));
      if (allMinutas.length === 1 && allMinutas[0]) {
        effectiveExecType = allMinutas[0].executionType;
        console.warn("[P0-02-V4] updateMinutaFromReport chamado sem executionType; inferido da única Minuta existente:", effectiveExecType);
      } else if (allMinutas.length > 1) {
        throw new AppError(ErrorCode.Validation, "executionType obrigatório quando existem múltiplas Minutas (PARADA/ROTINA). Especifique qual documento atualizar.");
      } else {
        throw new AppError(ErrorCode.NotFound, "Minuta not found", { safeMessage: "A Minuta ainda não foi gerada." });
      }
    }
    const minuta = await this.minutaService.getMinutaByInspectionAndExecutionType(context, String(report.inspectionId), effectiveExecType);
    if (!minuta) throw new AppError(ErrorCode.NotFound, `Minuta ${effectiveExecType} não encontrada`, { safeMessage: `A Minuta ${effectiveExecType} ainda não foi gerada.` });
    const description = await this.describeReport(context, report);
    // P0-02 V4 Etapa 3: filtrar recomendações pelo mesmo executionType da Minuta.
    const allRecommendations = [...await this.reportService.listRecommendationsByReport(context, reportId)];
    const normalizeExec = (r: any): 'PARADA' | 'ROTINA' => {
      const raw = (r.executeIn || r.executionType || r.execution || '').toString().toUpperCase();
      if (raw === 'SHUTDOWN' || raw === 'PARADA') return 'PARADA';
      return 'ROTINA';
    };
    const recommendations = allRecommendations.filter(r => normalizeExec(r) === effectiveExecType);
    const source = this.minutaSourceOf(report, description, recommendations);
    const revisions = await this.minutaService.getRevisions(context, minuta.id);
    const current = revisions.find(r => r.status === "ATUAL") ?? revisions[revisions.length - 1];
    const contextData = {
      clientName: description.client || String(report.clientId), projectName: description.project || "N/A", unitArea: description.unitArea || "N/A",
      osNumber: [description.osNumber, description.subOsNumber].filter(Boolean).join(" / ") || "N/A", tag: description.tag || "N/A", equipmentType: description.equipmentType || "EQUIPAMENTO",
      projectCode: description.projectCode || "N/A", fabricationCode: description.fabricationCode || "N/A", fabricationStandard: description.fabricationStandard || "N/A",
      materialSpec: description.materialSpec || "N/A", systemName: description.systemName || "N/A",
      inspectorName: description.inspectorName || "Inspetor Responsável", tenantLogo: await this.minutaLogo(),
      // P0-05: trackingNumber/revisionNumber devem chegar ao modelo para evitar "N/A" / "RASCUNHO"
      // quando a Minuta já possui numeração oficial. O serviço injeta os valores reais logo
      // após buildMinutaDocumentModel, mas o preview/rebuild precisa deles já no contextData.
      trackingNumber: minuta.trackingNumber,
      revisionNumber: minuta.currentRevision,
      ...(this.minutaAssessmentOf(report.engineeringAssessment) ? { assessment: this.minutaAssessmentOf(report.engineeringAssessment)! } : {})
    };
    const model = buildMinutaDocumentModel(contextData, recommendations, current?.snapshotData);
    await this.minutaService.createUpdateRevision(context, minuta.id, model, reason, source);
    if (this.audit) {
      await this.audit.record(context, { action: AuditAction.MinuteEdited, entityType: "MINUTA", entityId: minuta.id, previousState: { revision: minuta.currentRevision, sourceHash: minuta.lastSourceHash ?? null } as any, newState: { sourceHash: source.sourceHash } as any, metadata: { reportId: String(report.id), reason, recommendations: recommendations.length, performedBy: context.userId || "system" } });
    }
    // P0-02 V4 Etapa 4: retornar contexto do mesmo executionType atualizado.
    return this.getMinutaTecnicaContext(context, reportId, effectiveExecType);
  }

  /** SALVAR RASCUNHO (observações da Engenharia na Minuta). */
  async saveMinutaDraft(context: RequestContext, reportId: IntegrityReportId, input: { engineeringNotes: string }, executionType?: 'PARADA' | 'ROTINA'): Promise<MinutaTecnicaContext> {
    this.auth.require(context, PermissionCode.EngineeringMinuteEdit);
    if (!this.minutaService) throw new AppError(ErrorCode.NotFound, "MinutaService not available");
    const ctx = await this.getMinutaTecnicaContext(context, reportId, executionType);
    if (!ctx.minuta) throw new AppError(ErrorCode.NotFound, "Minuta not found", { safeMessage: "A Minuta ainda não foi gerada." });
    await this.minutaService.saveEngineeringNotes(context, ctx.minuta.id, input.engineeringNotes);
    if (this.audit) await this.audit.record(context, { action: AuditAction.MinuteEdited, entityType: "MINUTA", entityId: ctx.minuta.id, previousState: null, newState: { engineeringNotes: input.engineeringNotes } as any, metadata: { reportId: String(reportId), field: "engineeringNotes", performedBy: context.userId || "system" } });
    return this.getMinutaTecnicaContext(context, reportId, executionType);
  }

  /** VALIDAR MINUTA pela Minuta Técnica: primeiro as validações do bloco (motivos exatos), depois a validação existente (CONFORME = MINUTA VALIDADA). */
  async validateMinutaTecnica(context: RequestContext, reportId: IntegrityReportId, executionType?: 'PARADA' | 'ROTINA'): Promise<MinutaTecnicaContext> {
    let ctx = await this.getMinutaTecnicaContext(context, reportId, executionType);
    if (!ctx.minuta) throw new AppError(ErrorCode.NotFound, "Minuta not found", { safeMessage: "A Minuta ainda não foi gerada." });

    // Auto-recover legacy/unstarted minuta if report is in engineering analysis
    const report = await this.reportService.getReportById(context, reportId);
    if (report && ['UNDER_ENGINEERING_REVIEW', 'APPROVED', 'AWAITING_SIGNATURE', 'SIGNED', 'COMPLETED'].includes(report.status)) {
      if (ctx.minuta.status === 'EMITIDA' || ctx.minuta.status === 'AGUARDANDO ANÁLISE') {
        try {
          await this.startMinutaAnalysis(context, ctx.minuta.id);
          ctx = await this.getMinutaTecnicaContext(context, reportId, executionType);
        } catch (e) {
          console.warn("[MINUTA-VALIDATE-AUTO-START] Could not auto-start minuta:", e);
        }
      }
    }

    if (!ctx.minuta) throw new AppError(ErrorCode.NotFound, "Minuta not found", { safeMessage: "A Minuta ainda não foi gerada." });

    if (ctx.validationReasons.length > 0) {
      throw new AppError(ErrorCode.Validation, "Minuta not valid", { safeMessage: `A Minuta não pode ser validada: ${ctx.validationReasons.join("; ")}.` });
    }
    await this.validateMinuta(context, ctx.minuta.id);
    // P0-02 V4 Etapa 4: retornar contexto do mesmo executionType validado.
    return this.getMinutaTecnicaContext(context, reportId, executionType);
  }

  /** SOLICITAR CORREÇÃO pela Minuta: itens exatos (seção/item/observação) → Inspetor; recomendações também entram no ciclo da Minuta. */
  async requestMinutaCorrections(context: RequestContext, reportId: IntegrityReportId, items: TechnicalCorrectionItemInput[], executionType?: 'PARADA' | 'ROTINA'): Promise<{ corrections: TechnicalCorrectionRequest[]; minutaRequests: number }> {
    if (!this.minutaService) throw new AppError(ErrorCode.NotFound, "MinutaService not available");
    const report = await this.reportService.getReportById(context, reportId);
    if (!report) throw new AppError(ErrorCode.NotFound, "Relatório não encontrado.");
    const { validateCorrectionItems } = await import("../../domain/review/technical-correction.js");
    const errors = validateCorrectionItems(items);
    if (errors.length > 0) throw new AppError(ErrorCode.Validation, "Invalid items", { safeMessage: errors.join(" ") });
    // P0-02 V4 Etapa 7: buscar Minuta por executionType se fornecido, senão usar listByInspection e aplicar em todas.
    const allMinutas = executionType
      ? [await this.minutaService.getMinutaByInspectionAndExecutionType(context, String(report.inspectionId), executionType)]
      : await this.minutaService.listMinutasByInspection(context, String(report.inspectionId));
    let minutaRequests = 0;
    const recs = await this.reportService.listRecommendationsByReport(context, reportId);
    const normalizeExec = (r: any): 'PARADA' | 'ROTINA' => {
      const raw = (r.executeIn || r.executionType || r.execution || '').toString().toUpperCase();
      if (raw === 'SHUTDOWN' || raw === 'PARADA') return 'PARADA';
      return 'ROTINA';
    };
    for (const minuta of allMinutas.filter(Boolean)) {
      if (["EMITIDA", "AGUARDANDO ANÁLISE", "ATUALIZAÇÃO PENDENTE"].includes(minuta!.status)) await this.startMinutaAnalysis(context, minuta!.id).catch(() => undefined);
      for (const item of items) {
        if (item.sectionId !== "recommendations" || !item.itemId) continue;
        const rec = recs.find(r => String(r.id) === item.itemId);
        if (!rec) continue;
        // P0-02 V4: só registrar correção na Minuta do mesmo executionType da recomendação.
        if (executionType && normalizeExec(rec) !== executionType) continue;
        if (!executionType && normalizeExec(rec) !== minuta!.executionType) continue;
        try {
          await this.requestCorrectionToInspector(context, minuta!.id, String(rec.id), item.itemLabel || (rec as any).recommendationNumber || String(rec.id), item.comment, item.priority ? `Prioridade: ${item.priority}` : undefined);
          minutaRequests++;
        } catch (e) { console.warn("[MINUTA] correção da recomendação não registrada no ciclo da Minuta", e); }
      }
    }
    let corrections: TechnicalCorrectionRequest[] = [];
    if (this.corrections) {
      const description = await this.describeReport(context, report);
      const recipient = await this.corrections.recipientForProfessional(description.inspectorProfessionalId, description.inspectorName);
      const checklistRoute = `/inspections/checklist/${report.inspectionId}`;
      corrections = await this.corrections.requestCorrections(context, {
        documentType: "INTEGRITY", documentId: String(report.id), documentNumber: report.reportNumber || String(report.id), revision: report.revision || "00", clientId: String(report.clientId),
        documentRoute: `/reports/view/${report.id}`, sectionRoutes: { recommendations: checklistRoute, checklist: checklistRoute, photos: checklistRoute }, reviewId: null, assignedTo: recipient, items
      });
    }
    return { corrections, minutaRequests };
  }

  // ============================================================
  // FINALIZAR RELATÓRIO (RI)
  // ============================================================

  async finalizationGate(context: RequestContext, reportId: IntegrityReportId): Promise<FinalizationGate> {
    const report = await this.reportService.getReportById(context, reportId);
    if (!report) throw new AppError(ErrorCode.NotFound, "Relatório não encontrado.");
    const reasons: string[] = [];
    if (report.finalization) reasons.push("o relatório já está finalizado");
    if (!["APPROVED", "AWAITING_SIGNATURE", "SIGNED", "COMPLETED"].includes(report.status)) reasons.push("a análise técnica ainda não foi concluída (Validar Relatório)");
    const a = normalizeEngineeringAssessment(report.engineeringAssessment);
    if (!a.equipmentCondition) reasons.push("a Condição do Equipamento não foi definida");
    if (!a.residualLife || a.residualLife.adoptedMonths < 1 || a.residualLife.adoptedMonths > 240) reasons.push("a Vida Residual não é válida (1 a 240 meses)");
    // P0-02 V4 Etapa 8: finalizationGate deve considerar TODAS as Minutas da inspeção.
    // Se existem PARADA e ROTINA, ambas precisam estar validadas para liberar finalização.
    const minutas = this.minutaService ? await this.minutaService.listMinutasByInspection(context, String(report.inspectionId)).catch(() => [] as Minuta[]) : [];
    if (minutas.length === 0) {
      reasons.push("a Minuta Técnica ainda não foi gerada");
    } else {
      for (const m of minutas) {
        if (!["CONFORME", "ANÁLISE CONCLUÍDA", "APROVADA"].includes(m.status)) {
          reasons.push(`a Minuta ${m.executionType} ainda não foi validada (status: ${m.status})`);
        }
      }
    }
    if (this.corrections) {
      const pending = await this.corrections.listPendingByDocument(context, "INTEGRITY", String(report.id));
      if (pending.length > 0) reasons.push(`existem ${pending.length} correção(ões) pendente(s)`);
    }
    // Verificar correções pendentes em TODAS as Minutas da inspeção
    if (minutas.length > 0 && this.minutaService) {
      let totalOpenCorrections = 0;
      for (const m of minutas) {
        const mc = await this.minutaService.getCorrectionRequests(context, m.id).catch(() => [] as any[]);
        totalOpenCorrections += mc.filter((c: any) => ["PENDENTE", "EM CORREÇÃO", "CORRIGIDA", "AGUARDANDO VALIDAÇÃO", "DEVOLVIDA"].includes(c.status)).length;
      }
      if (totalOpenCorrections > 0) reasons.push(`existem ${totalOpenCorrections} correção(ões) da Minuta pendente(s)`);
    }
    if (this.holds) {
      const [h1, h2] = await Promise.all([this.holds.activeHold(context, "INTEGRITY", String(report.id)), this.holds.activeHold(context, "CHECKLIST", String(report.inspectionId))]);
      if (h1 || h2) reasons.push("o documento está EM HOLD");
    }
    const signed = report.status === "SIGNED" || report.status === "COMPLETED";
    return { allowed: reasons.length === 0, reasons, resultLabel: signed ? "FINALIZADO" : "FINALIZADO — AGUARDANDO ASSINATURA" };
  }

  /** FINALIZAR RELATÓRIO (RI): gate + snapshot documental final + bloqueio + auditoria. FINALIZAR ≠ ASSINAR. */
  async finalizeIntegrityReport(context: RequestContext, reportId: IntegrityReportId): Promise<{ report: IntegrityReport; gate: FinalizationGate }> {
    const gate = await this.finalizationGate(context, reportId);
    if (!gate.allowed) throw new AppError(ErrorCode.Conflict, "Cannot finalize", { safeMessage: `Não é possível finalizar: ${gate.reasons.join("; ")}.` });
    let snapshotId: string | null = null;
    try { snapshotId = (await this.reportService.buildDocumentSnapshot(context, reportId)).id; } catch { snapshotId = null; }
    const report = await this.reportService.finalizeIntegrityReport(context, reportId, { snapshotId });
    return { report, gate };
  }

  /**
   * MANDAR PARA CORREÇÃO com itens: devolve o RI ao Inspetor (workflow do RI)
   * e registra cada item com destino exato (Checklist → Recomendações → REC-XXX
   * quando a seção é Recomendações). O Inspetor responsável é o da Inspeção.
   */
  async returnForCorrectionWithItems(context: RequestContext, reportId: IntegrityReportId, items: TechnicalCorrectionItemInput[], summary?: string): Promise<{ report: IntegrityReport; corrections: TechnicalCorrectionRequest[] }> {
    const report = await this.reportService.getReportById(context, reportId);
    if (!report) throw new AppError(ErrorCode.NotFound, "Relatório não encontrado.");
    const description = await this.describeReport(context, report);
    const lines = items.map((i, n) => `${String(n + 1).padStart(2, "0")} ${i.sectionLabel || sectionLabelOf("INTEGRITY", i.sectionId)}${i.itemId ? ` · ${i.itemLabel || i.itemId}` : ""}: ${(i.comment || "").trim()}`);
    const comment = [summary?.trim(), ...lines].filter(Boolean).join("\n");
    const returned = await this.reportService.returnByPlh(context, reportId, comment || "Correção solicitada pelo PLH.");
    let corrections: TechnicalCorrectionRequest[] = [];
    if (this.corrections && items.length > 0) {
      const recipient = await this.corrections.recipientForProfessional(description.inspectorProfessionalId, description.inspectorName);
      const checklistRoute = `/inspections/checklist/${report.inspectionId}`;
      corrections = await this.corrections.requestCorrections(context, {
        documentType: "INTEGRITY", documentId: String(report.id), documentNumber: report.reportNumber || String(report.id), revision: report.revision || "00", clientId: String(report.clientId),
        documentRoute: `/reports/view/${report.id}`,
        // Recomendações e Checklist abrem no Checklist da inspeção (REC destacada); o resto no RI.
        sectionRoutes: { recommendations: checklistRoute, checklist: checklistRoute, photos: checklistRoute },
        reviewId: null, assignedTo: recipient, items
      });
    }
    return { report: returned, corrections };
  }

  // ============================================================
  // MINUTA ANALYSIS FLOW
  // ============================================================

  async getMinutaWorkspaceContext(
    context: RequestContext,
    minutaId: string
  ): Promise<MinutaWorkspaceContext> {
    if (!this.minutaService) throw new AppError(ErrorCode.NotFound, 'MinutaService not available');

    const minuta = await this.minutaService.getMinuta(context, minutaId);
    if (!minuta) throw new AppError(ErrorCode.NotFound, 'Minuta not found');

    const corrections = await this.minutaService.getCorrectionRequests(context, minutaId);
    const revisions = await this.minutaService.getRevisions(context, minutaId);
    
    // Load live recommendations for this inspection
    let recommendations: any[] = [];
    if (this.recommendationRepo && this.recommendationRepo.findByInspection) {
      try {
        const allRecs = await this.recommendationRepo.findByInspection(context, minuta.inspectionId as any);
        recommendations = allRecs ? [...allRecs] : [];
      } catch {
        recommendations = [];
      }
    }

    const pendencySummary = this.calculatePendencySummary(corrections, minuta);

    return {
      minuta,
      recommendations,
      corrections,
      revisions: revisions as any[],
      pendencySummary
    };
  }

  private calculatePendencySummary(
    corrections: readonly MinutaCorrectionRequest[], 
    minuta: Minuta
  ): PendencySummary {
    const pending = corrections.filter(c => c.status === 'PENDENTE').length;
    const inCorrection = corrections.filter(c => c.status === 'EM CORREÇÃO').length;
    const corrected = corrections.filter(c => c.status === 'CORRIGIDA').length;
    const awaitingValidation = corrections.filter(c => c.status === 'AGUARDANDO VALIDAÇÃO').length;
    const validated = corrections.filter(c => c.status === 'VALIDADA').length;
    const returned = corrections.filter(c => c.status === 'DEVOLVIDA').length;
    const awaitingInspector = pending + inCorrection + returned;

    const hasUnresolved = pending > 0 || inCorrection > 0 || corrected > 0 || awaitingValidation > 0 || returned > 0;
    const canComplete = !hasUnresolved && minuta.status === 'EM ANÁLISE';
    // Accepts the new 'CONFORME' status (Bloco 6) as well as the deprecated
    // legacy 'ANÁLISE CONCLUÍDA' value, so already-persisted Minutas from
    // before the rename can still be approved.
    const canApprove = minuta.status === 'CONFORME' || minuta.status === 'ANÁLISE CONCLUÍDA';

    return {
      totalCorrections: corrections.length,
      pending,
      inCorrection,
      corrected,
      awaitingValidation,
      validated,
      returned,
      awaitingInspector,
      canComplete,
      canApprove
    };
  }

  async startMinutaAnalysis(
    context: RequestContext,
    minutaId: string
  ): Promise<Minuta> {
    this.auth.require(context, PermissionCode.EngineeringStartAnalysis);
    if (!this.minutaService) throw new AppError(ErrorCode.NotFound, 'MinutaService not available');

    const minuta = await this.minutaService.getMinuta(context, minutaId);
    if (!minuta) throw new AppError(ErrorCode.NotFound, 'Minuta not found');

    // Allow starting from EMITIDA, AGUARDANDO ANÁLISE, or CORREÇÃO NECESSÁRIA
    // (Bloco 9: re-entering analysis after a whole-Minuta return).
    const allowedStatuses = ['EMITIDA', 'AGUARDANDO ANÁLISE', 'CORREÇÃO NECESSÁRIA'];
    if (!allowedStatuses.includes(minuta.status)) {
      if (minuta.status === 'EM ANÁLISE') return minuta; // Already started
      throw new AppError(ErrorCode.Conflict, `Minuta não está apta para iniciar análise. Status atual: ${minuta.status}`);
    }

    // Check concurrency: if another engineer already started
    if (minuta.analysisStartedBy && minuta.analysisStartedBy !== context.userId) {
      throw new AppError(ErrorCode.Conflict, `Análise já iniciada por outro engenheiro (${minuta.analysisStartedBy}).`);
    }

    const previousState = { ...minuta };

    minuta.status = 'EM ANÁLISE';
    minuta.analysisStartedBy = context.userId || 'SYSTEM';
    minuta.analysisStartedAt = new Date();
    minuta.updatedAt = new Date();

    // Try to resolve professionalId from userId
    minuta.analysisProfessionalId = context.userId || undefined;

    await this.minutaService.minutaRepo.save(context, minuta);

    if (this.audit) {
      await this.audit.record(context, {
        action: AuditAction.EngineeringAnalysisStarted,
        entityType: 'MINUTA',
        entityId: minuta.id,
        previousState: previousState as any,
        newState: minuta as any,
        metadata: { trackingNumber: minuta.trackingNumber, engineer: minuta.analysisStartedBy }
      });
    }

    return minuta;
  }

  async editRecommendation(
    context: RequestContext,
    minutaId: string,
    recommendationId: string,
    payload: UpdateRecommendationFullPayload
  ): Promise<{ recommendation: Recommendation; minuta: Minuta }> {
    this.auth.require(context, PermissionCode.EngineeringEditRecommendation);
    if (!this.minutaService) throw new AppError(ErrorCode.NotFound, 'MinutaService not available');

    const minuta = await this.minutaService.getMinuta(context, minutaId);
    if (!minuta) throw new AppError(ErrorCode.NotFound, 'Minuta not found');

    if (minuta.status !== 'EM ANÁLISE') {
      throw new AppError(ErrorCode.Conflict, 'A Minuta deve estar EM ANÁLISE para edição.');
    }

    // Capture previous state before edit
    let previousRec: any = null;
    if (this.recommendationRepo) {
      previousRec = await this.recommendationRepo.findById(context, recommendationId as any);
      if (previousRec) previousRec = { ...previousRec };
    }

    // Update the original Recommendation via ReportService (source of truth)
    payload.correctionSource = 'ENGINEERING';
    const updatedRec = await this.reportService.updateRecommendationFull(
      context,
      recommendationId as RecommendationId,
      payload
    );

    // Check if there's a pending correction for this recommendation and auto-validate
    const corrections = await this.minutaService.getCorrectionRequests(context, minutaId);
    const pendingCorrection = corrections.find(
      c => c.recommendationId === recommendationId && 
      (c.status === 'PENDENTE' || c.status === 'EM CORREÇÃO')
    );
    if (pendingCorrection) {
      pendingCorrection.status = 'VALIDADA';
      pendingCorrection.correctedBy = context.userId || 'SYSTEM';
      pendingCorrection.correctedAt = new Date();
      pendingCorrection.validatedBy = context.userId || 'SYSTEM';
      pendingCorrection.validatedAt = new Date();
      pendingCorrection.correctionSource = 'ENGINEERING';
      pendingCorrection.previousState = previousRec;
      pendingCorrection.newState = { ...updatedRec };
      pendingCorrection.updatedAt = new Date();
      await this.minutaService.minutaRepo.saveCorrectionRequest(context, pendingCorrection);
    }

    minuta.updatedAt = new Date();
    await this.minutaService.minutaRepo.save(context, minuta);

    if (this.audit) {
      await this.audit.record(context, {
        action: AuditAction.EngineeringRecommendationEdited,
        entityType: 'RECOMMENDATION',
        entityId: recommendationId,
        previousState: previousRec as any,
        newState: updatedRec as any,
        metadata: { minutaId, correctedBy: context.userId, source: 'ENGINEERING' }
      });
      // Painel de Engenharia: a Minuta foi editada (evento MINUTE_EDITED, além do da Recommendation).
      await this.audit.record(context, {
        action: AuditAction.MinuteEdited,
        entityType: 'MINUTA',
        entityId: minutaId,
        previousState: null,
        newState: { recommendationId, trackingNumber: minuta.trackingNumber, revision: minuta.currentRevision } as any,
        metadata: { minutaId, recommendationId, performedBy: context.userId || 'system', at: new Date().toISOString() }
      });
    }

    return { recommendation: updatedRec, minuta };
  }

  async requestCorrectionToInspector(
    context: RequestContext,
    minutaId: string,
    recommendationId: string,
    visualIndex: string,
    reason: string,
    observation?: string
  ): Promise<MinutaCorrectionRequest> {
    // "Solicitar Correção" and "Enviar ao Inspetor" are the same single
    // action in the current model: creating a correction request always
    // routes it to the Inspection's registered inspector immediately
    // (Fase 14 + 16, Bloco 6). Both permissions must be held.
    this.auth.require(context, PermissionCode.EngineeringRequestCorrection);
    this.auth.require(context, PermissionCode.EngineeringDelegateToInspector);
    if (!this.minutaService) throw new AppError(ErrorCode.NotFound, 'MinutaService not available');

    const minuta = await this.minutaService.getMinuta(context, minutaId);
    if (!minuta) throw new AppError(ErrorCode.NotFound, 'Minuta not found');

    if (minuta.status !== 'EM ANÁLISE' && minuta.status !== 'CORREÇÃO NECESSÁRIA' && minuta.status !== 'AGUARDANDO INSPETOR') {
      throw new AppError(ErrorCode.Conflict, 'A Minuta deve estar EM ANÁLISE para solicitar correções.');
    }

    // Capture current recommendation state
    let currentState: any = null;
    if (this.recommendationRepo) {
      const rec = await this.recommendationRepo.findById(context, recommendationId as any);
      if (rec) currentState = { ...rec };
    }

    // Resolve the real inspector from the Inspection (Fase 16/17): never
    // send generically — the destination is always the inspector actually
    // registered on this Inspection, looked up by inspectionId, never by
    // free text or array position.
    let resolvedInspectorId: string | null = null;
    if (this.reportService.inspectionRepo) {
      const inspection = await this.reportService.inspectionRepo.findById(context, minuta.inspectionId as any);
      resolvedInspectorId = inspection?.inspectorId ?? null;
    }
    if (!resolvedInspectorId) {
      throw new AppError(ErrorCode.Validation, 'Não foi possível identificar o Inspetor responsável pela Inspeção desta Minuta.', {
        safeMessage: 'A Inspeção correspondente não possui um Inspetor cadastrado. Não é possível enviar a correção.'
      });
    }

    const correction: MinutaCorrectionRequest = {
      id: `CORREQ-${randomUUID()}`,
      minutaId,
      recommendationId,
      visualIndex,
      reason,
      observation: observation || undefined,
      status: 'PENDENTE',
      requesterId: context.userId || 'SYSTEM',
      inspectorId: resolvedInspectorId,
      previousState: currentState,
      correctionSource: undefined,
      createdAt: new Date(),
      updatedAt: new Date()
    };

    await this.minutaService.minutaRepo.saveCorrectionRequest(context, correction);

    // Update minuta status
    minuta.status = 'AGUARDANDO INSPETOR';
    minuta.updatedAt = new Date();
    await this.minutaService.minutaRepo.save(context, minuta);

    if (this.audit) {
      await this.audit.record(context, {
        action: AuditAction.EngineeringCorrectionRequested,
        entityType: 'MINUTA_CORRECTION',
        entityId: correction.id,
        previousState: null,
        newState: correction as any,
        metadata: { minutaId, recommendationId, reason }
      });
    }

    return correction;
  }

  async submitInspectorCorrection(
    context: RequestContext,
    minutaId: string,
    correctionId: string,
    updatedRecommendationState?: any
  ): Promise<MinutaCorrectionRequest> {
    // Submitted by the Inspector, not the Engineer — gated by the
    // Recommendation-update permission already granted to INSPECTOR
    // (not a new engineering.* permission, since this is the Inspector's
    // own recommendation-correction capability, not an engineering one).
    this.auth.require(context, PermissionCode.RecommendationUpdate);
    if (!this.minutaService) throw new AppError(ErrorCode.NotFound, 'MinutaService not available');

    const corrections = await this.minutaService.getCorrectionRequests(context, minutaId);
    const correction = corrections.find(c => c.id === correctionId);
    if (!correction) throw new AppError(ErrorCode.NotFound, 'Correction request not found');

    correction.status = 'AGUARDANDO VALIDAÇÃO';
    correction.correctedBy = context.userId || 'SYSTEM';
    correction.correctedAt = new Date();
    correction.correctionSource = 'INSPECTOR';
    correction.newState = updatedRecommendationState || null;
    correction.updatedAt = new Date();

    await this.minutaService.minutaRepo.saveCorrectionRequest(context, correction);

    // Check if minuta status should change
    const minuta = await this.minutaService.getMinuta(context, minutaId);
    if (minuta) {
      const allCorrections = await this.minutaService.getCorrectionRequests(context, minutaId);
      const stillPending = allCorrections.some(c => c.status === 'PENDENTE' || c.status === 'EM CORREÇÃO' || c.status === 'DEVOLVIDA');
      
      if (!stillPending) {
        minuta.status = 'AGUARDANDO VALIDAÇÃO';
      } else {
        minuta.status = 'AGUARDANDO INSPETOR';
      }
      minuta.updatedAt = new Date();
      await this.minutaService.minutaRepo.save(context, minuta);
    }

    if (this.audit) {
      await this.audit.record(context, {
        action: AuditAction.InspectorCorrectionSubmitted,
        entityType: 'MINUTA_CORRECTION',
        entityId: correction.id,
        previousState: { status: 'PENDENTE' } as any,
        newState: correction as any,
        metadata: { minutaId, correctedBy: context.userId }
      });
    }

    return correction;
  }

  async validateCorrection(
    context: RequestContext,
    minutaId: string,
    correctionId: string
  ): Promise<MinutaCorrectionRequest> {
    this.auth.require(context, PermissionCode.EngineeringValidateCorrection);
    if (!this.minutaService) throw new AppError(ErrorCode.NotFound, 'MinutaService not available');

    const corrections = await this.minutaService.getCorrectionRequests(context, minutaId);
    const correction = corrections.find(c => c.id === correctionId);
    if (!correction) throw new AppError(ErrorCode.NotFound, 'Correction request not found');

    if (correction.status !== 'AGUARDANDO VALIDAÇÃO' && correction.status !== 'CORRIGIDA') {
      throw new AppError(ErrorCode.Conflict, 'A correção deve estar AGUARDANDO VALIDAÇÃO para ser validada.');
    }

    correction.status = 'VALIDADA';
    correction.validatedBy = context.userId || 'SYSTEM';
    correction.validatedAt = new Date();
    correction.updatedAt = new Date();

    await this.minutaService.minutaRepo.saveCorrectionRequest(context, correction);

    // Recalculate minuta status
    const minuta = await this.minutaService.getMinuta(context, minutaId);
    if (minuta) {
      const allCorrections = await this.minutaService.getCorrectionRequests(context, minutaId);
      const pendencySummary = this.calculatePendencySummary(allCorrections, minuta);
      
      if (pendencySummary.canComplete) {
        minuta.status = 'EM ANÁLISE'; // Ready for completion
      }
      minuta.updatedAt = new Date();
      await this.minutaService.minutaRepo.save(context, minuta);
    }

    if (this.audit) {
      await this.audit.record(context, {
        action: AuditAction.EngineeringCorrectionValidated,
        entityType: 'MINUTA_CORRECTION',
        entityId: correction.id,
        previousState: { status: 'AGUARDANDO VALIDAÇÃO' } as any,
        newState: correction as any,
        metadata: { minutaId, validatedBy: context.userId }
      });
    }

    return correction;
  }

  /**
   * "Validar Item" (Bloco 9, Checklist & Fotos): Engineering confirms a
   * checklist item's finding/Recommendation is technically adequate, WITHOUT
   * requiring a correction cycle first (Fase 38/39 — "validado" is not the
   * same as "sem anomalia"; an item with a real anomaly and a correct
   * Recommendation can be validated as-is). Reuses the existing
   * MinutaCorrectionRequest/saveCorrectionRequest infrastructure — no new
   * domain concept — just recorded straight at status VALIDADA, with no
   * Inspector round-trip.
   */
  async validateChecklistItem(
    context: RequestContext,
    minutaId: string,
    recommendationId: string
  ): Promise<MinutaCorrectionRequest> {
    this.auth.require(context, PermissionCode.EngineeringValidateCorrection);
    if (!this.minutaService) throw new AppError(ErrorCode.NotFound, 'MinutaService not available');

    const minuta = await this.minutaService.getMinuta(context, minutaId);
    if (!minuta) throw new AppError(ErrorCode.NotFound, 'Minuta not found');

    const corrections = await this.minutaService.getCorrectionRequests(context, minutaId);
    const existing = corrections.find(c => c.recommendationId === recommendationId && c.status !== 'VALIDADA');
    if (existing) {
      // A correction is already in flight for this item — validate that one
      // instead of creating a second, parallel record for the same item.
      return this.validateCorrection(context, minutaId, existing.id);
    }

    const record: MinutaCorrectionRequest = {
      id: `CORREQ-${randomUUID()}`,
      minutaId,
      recommendationId,
      visualIndex: '-',
      reason: 'Validação direta pela Engenharia (constatação/Recommendation tecnicamente adequados, sem necessidade de correção).',
      status: 'VALIDADA',
      requesterId: context.userId || 'SYSTEM',
      inspectorId: null,
      validatedBy: context.userId || 'SYSTEM',
      validatedAt: new Date(),
      createdAt: new Date(),
      updatedAt: new Date()
    };
    await this.minutaService.minutaRepo.saveCorrectionRequest(context, record);

    if (this.audit) {
      await this.audit.record(context, {
        action: AuditAction.EngineeringCorrectionValidated,
        entityType: 'MINUTA_CORRECTION',
        entityId: record.id,
        previousState: null,
        newState: record as any,
        metadata: { minutaId, recommendationId, validatedBy: context.userId, direct: true }
      });
    }

    return record;
  }

  async returnCorrectionToInspector(
    context: RequestContext,
    minutaId: string,
    correctionId: string,
    reason: string
  ): Promise<MinutaCorrectionRequest> {
    this.auth.require(context, PermissionCode.EngineeringValidateCorrection);
    if (!this.minutaService) throw new AppError(ErrorCode.NotFound, 'MinutaService not available');

    const corrections = await this.minutaService.getCorrectionRequests(context, minutaId);
    const correction = corrections.find(c => c.id === correctionId);
    if (!correction) throw new AppError(ErrorCode.NotFound, 'Correction request not found');

    correction.status = 'DEVOLVIDA';
    correction.reason = correction.reason + `\n[DEVOLVIDA] ${reason}`;
    correction.updatedAt = new Date();

    await this.minutaService.minutaRepo.saveCorrectionRequest(context, correction);

    // Update minuta status
    const minuta = await this.minutaService.getMinuta(context, minutaId);
    if (minuta) {
      minuta.status = 'AGUARDANDO INSPETOR';
      minuta.updatedAt = new Date();
      await this.minutaService.minutaRepo.save(context, minuta);
    }

    return correction;
  }

  /**
   * "Validar Minuta" = dar a Minuta como CONFORME (Bloco 6, 2026-09-14).
   * Replaces the old "Concluir Análise" (completeAnalysis) — there is no
   * separate "conclude" step between validating and PLH approval anymore;
   * validating IS declaring conformidade.
   */
  async validateMinuta(
    context: RequestContext,
    minutaId: string
  ): Promise<Minuta> {
    this.auth.require(context, PermissionCode.EngineeringValidateMinuta);
    if (!this.minutaService) throw new AppError(ErrorCode.NotFound, 'MinutaService not available');

    let minuta = await this.minutaService.getMinuta(context, minutaId);
    if (!minuta) throw new AppError(ErrorCode.NotFound, 'Minuta not found');

    if (minuta.status === 'EMITIDA' || minuta.status === 'AGUARDANDO ANÁLISE') {
      minuta = await this.startMinutaAnalysis(context, minutaId);
    }

    if (minuta.status !== 'EM ANÁLISE') {
      throw new AppError(ErrorCode.Conflict, 'A Minuta deve estar EM ANÁLISE para ser validada como Conforme.');
    }

    // Gate 1: no unresolved corrections of any kind (Fase 25).
    const corrections = await this.minutaService.getCorrectionRequests(context, minutaId);
    const pendencySummary = this.calculatePendencySummary(corrections, minuta);
    if (!pendencySummary.canComplete) {
      const parts: string[] = [];
      if (pendencySummary.awaitingInspector > 0) parts.push(`${pendencySummary.awaitingInspector} correção(ões) aguardando o Inspetor`);
      if (pendencySummary.awaitingValidation > 0) parts.push(`${pendencySummary.awaitingValidation} correção(ões) aguardando validação da Engenharia`);
      if (pendencySummary.corrected > 0) parts.push(`${pendencySummary.corrected} correção(ões) corrigida(s) ainda não validada(s)`);
      throw new AppError(
        ErrorCode.Validation,
        `Existem pendências não resolvidas: ${parts.join('; ')}. A Minuta não pode ser validada como Conforme.`
      );
    }

    // Gate 2: Check existing recommendations for missing ZR numbers (ONLY BLOCKER for validation)
    let liveRecs: any[] = [];
    if (this.recommendationRepo && this.recommendationRepo.findByInspection) {
      liveRecs = [...await this.recommendationRepo.findByInspection(context, minuta.inspectionId as any).catch(() => [])];
    }
    const missingZr = (liveRecs || []).filter(r => {
      const zrVal = r.zr ?? (r as any).zrNumber ?? (r as any).zoneRecommendationNumber;
      return zrVal === null || zrVal === undefined || String(zrVal).trim().length === 0 || String(zrVal).trim() === "undefined" || String(zrVal).trim() === "null";
    });
    if (missingZr.length > 0) {
      const missingLabels = missingZr.map(r => r.recommendationNumber || r.code || r.id).join(', ');
      throw new AppError(
        ErrorCode.Validation,
        `Não é possível validar a Minuta. A recomendação ${missingLabels} não possui Nº ZR informado.`,
        { safeMessage: `Não é possível validar a Minuta. A recomendação ${missingLabels} não possui Nº ZR informado.` }
      );
    }

    // Gate 3: required documentary fields present in the frozen snapshot
    // (Fase 26).
    const revisions = await this.minutaService.getRevisions(context, minutaId);
    const currentRev = revisions.find(r => r.status === 'ATUAL');
    const snapshotModel: any = currentRev?.snapshotData?.data || currentRev?.snapshotData || null;
    const missingFields = REQUIRED_MINUTA_DOCUMENT_FIELDS.filter(
      (field) => !snapshotModel || !String(snapshotModel[field.key] ?? '').trim()
    );
    if (missingFields.length > 0) {
      throw new AppError(
        ErrorCode.Validation,
        `Dados documentais obrigatórios ausentes: ${missingFields.map(f => f.label).join(', ')}. A Minuta não pode ser validada como Conforme.`
      );
    }

    const previousState = { ...minuta };
    minuta.status = 'CONFORME';
    minuta.analysisCompletedAt = new Date();
    minuta.validatedAt = new Date();
    minuta.validatedByUserId = context.userId || 'SYSTEM';
    minuta.validatedByProfessionalId = context.professionalId ?? undefined;
    minuta.updatedAt = new Date();

    await this.minutaService.minutaRepo.save(context, minuta);

    if (this.audit) {
      await this.audit.record(context, {
        action: AuditAction.EngineeringMinutaValidated,
        entityType: 'MINUTA',
        entityId: minuta.id,
        previousState: previousState as any,
        newState: minuta as any,
        metadata: {
          trackingNumber: minuta.trackingNumber,
          revision: minuta.currentRevision,
          validatedBy: context.userId
        }
      });
    }

    return minuta;
  }

  /**
   * "Reprovar / Devolver Minuta" (Bloco 9) — a whole-Minuta return, distinct
   * from a per-Recommendation MinutaCorrectionRequest (requestCorrectionToInspector
   * above). Used when the Engineer needs to send the entire document back
   * (e.g. to Documentação) rather than flag one specific Recommendation.
   */
  async returnMinutaForCorrection(
    context: RequestContext,
    minutaId: string,
    reason: string,
    target: string
  ): Promise<Minuta> {
    this.auth.require(context, PermissionCode.MinutaReturnForCorrection);
    if (!this.minutaService) throw new AppError(ErrorCode.NotFound, 'MinutaService not available');
    if (!reason || !reason.trim()) {
      throw new AppError(ErrorCode.Validation, 'O motivo da devolução é obrigatório.', {
        safeMessage: 'Informe o motivo da devolução.'
      });
    }

    const minuta = await this.minutaService.getMinuta(context, minutaId);
    if (!minuta) throw new AppError(ErrorCode.NotFound, 'Minuta not found');

    if (minuta.status !== 'EM ANÁLISE' && minuta.status !== 'AGUARDANDO VALIDAÇÃO') {
      throw new AppError(ErrorCode.Conflict, 'A Minuta deve estar EM ANÁLISE ou AGUARDANDO VALIDAÇÃO para ser devolvida.');
    }

    const previousState = { ...minuta };
    minuta.status = 'CORREÇÃO NECESSÁRIA';
    minuta.returnedAt = new Date();
    minuta.returnedByUserId = context.userId || 'SYSTEM';
    minuta.returnReason = reason.trim();
    minuta.returnTarget = target;
    minuta.updatedAt = new Date();

    await this.minutaService.minutaRepo.save(context, minuta);

    if (this.audit) {
      await this.audit.record(context, {
        action: AuditAction.MinutaReturnedForCorrection,
        entityType: 'MINUTA',
        entityId: minuta.id,
        previousState: previousState as any,
        newState: minuta as any,
        metadata: { trackingNumber: minuta.trackingNumber, reason, target }
      });
    }

    return minuta;
  }

  async approveAndSignMinuta(
    context: RequestContext,
    minutaId: string
  ): Promise<Minuta> {
    this.auth.require(context, PermissionCode.EngineeringApproveMinuta);
    this.auth.require(context, PermissionCode.EngineeringSignMinuta);
    if (!this.minutaService) throw new AppError(ErrorCode.NotFound, 'MinutaService not available');

    const minuta = await this.minutaService.getMinuta(context, minutaId);
    if (!minuta) throw new AppError(ErrorCode.NotFound, 'Minuta not found');

    // 'ANÁLISE CONCLUÍDA' accepted only for Minutas already at that legacy
    // status before Bloco 6 — new transitions always produce 'CONFORME'.
    if (minuta.status !== 'CONFORME' && (minuta.status as string) !== 'ANÁLISE CONCLUÍDA') {
      throw new AppError(ErrorCode.Conflict, 'A Minuta deve estar CONFORME para ser aprovada e assinada.');
    }

    // Double-check no pending corrections
    const corrections = await this.minutaService.getCorrectionRequests(context, minutaId);
    const pendencySummary = this.calculatePendencySummary(corrections, minuta);
    if (!pendencySummary.canApprove) {
      throw new AppError(ErrorCode.Validation, 'A Minuta possui pendências não resolvidas e não pode ser aprovada.');
    }

    // PLH gate (Fase 32/33): ENGINEER or ADMIN alone never signs — the
    // acting user must resolve to a qualified, active Professional
    // registered as a PLH for the DOCUMENT's client. See
    // src/domain/identity/plh-eligibility.ts — every check reads a real,
    // already-modeled field; nothing is invented.
    //
    // O Cliente vem do DOCUMENTO (Minuta → Projeto → Cliente), nunca do
    // Cliente selecionado na sessão. Usar `context.activeClientId` recusava o
    // PLH correto sempre que a tela estivesse com outro Cliente — ou nenhum —
    // selecionado, e ao mesmo tempo fazia a autorização depender de um valor
    // que a interface muda à vontade. A seleção da sessão serve para navegar
    // e filtrar; não decide quem assina.
    const documentClientId = await this.clientOfMinuta(minuta);
    // O gate compara com o PLH que o CLIENTE nomeou, não com o id do Cliente.
    const clientResponsiblePlhId = await this.responsiblePlhOfClient(documentClientId);
    const identityRepo = this.reportService.identityRepo as any;
    const user = identityRepo && context.userId ? await identityRepo.getUser(context.userId) : null;
    const professional = identityRepo && user?.professionalId ? await identityRepo.getProfessional(user.professionalId) : null;
    const eligibility = canActAsPlh(user, professional, clientResponsiblePlhId);
    if (!eligibility.allowed) {
      throw new AppError(ErrorCode.Authorization, eligibility.reason || 'Usuário não habilitado como PLH.', {
        safeMessage: eligibility.reason || 'Você não está habilitado a aprovar e assinar como PLH.'
      });
    }

    const previousState = { ...minuta };

    // Generate a SHA-256 hash of the COMPLETE canonical document snapshot —
    // not a truncated fragment (Fase 36; the pre-Bloco-6 implementation
    // hashed only JSON.stringify(snapshotData).substring(0, 500), a weak
    // integrity guarantee for any non-trivial Minuta).
    const revisions = await this.minutaService.getRevisions(context, minutaId);
    const currentRev = revisions.find(r => r.status === 'ATUAL');
    let hashSource = `${minuta.trackingNumber}|${minuta.currentRevision}|${new Date().toISOString()}`;
    if (currentRev?.snapshotData) {
      hashSource += `|${JSON.stringify(currentRev.snapshotData)}`;
    }

    let hash = '';
    try {
      const encoder = new TextEncoder();
      const data = encoder.encode(hashSource);
      const hashBuffer = await crypto.subtle.digest('SHA-256', data);
      const hashArray = Array.from(new Uint8Array(hashBuffer));
      hash = hashArray.map(b => b.toString(16).padStart(2, '0')).join('');
    } catch {
      hash = `HASH-${randomUUID()}`;
    }

    const approvedAt = new Date();

    minuta.status = 'APROVADA';
    minuta.approvedBy = context.userId || 'SYSTEM';
    minuta.approvedByProfessionalId = user?.professionalId ?? undefined;
    minuta.approvedAt = approvedAt;
    minuta.signatureHash = hash;
    minuta.updatedAt = new Date();

    await this.minutaService.minutaRepo.save(context, minuta);

    // Freeze the PLH signature into THIS revision's snapshot (Fase 25/29):
    // the image, name and registration used are the ones the Professional
    // has right now, at the moment of approval — captured into the frozen
    // document model so a later change to the Professional's signature
    // never alters how an already-approved revision is rendered/printed.
    // Building this from the Professional record already resolved above
    // (never the User's photo/avatar — only the registered Professional
    // qualification fields, same pattern as the RI signatories block in
    // report-service.ts).
    if (currentRev?.snapshotData) {
      let signatureImage: string | null = null;
      if (identityRepo?.getProfessionalSignature && professional?.id) {
        try { signatureImage = await identityRepo.getProfessionalSignature(professional.id); } catch { signatureImage = null; }
      }
      const registrationParts: string[] = [];
      if (professional?.crea) {
        registrationParts.push(String(professional.crea).toUpperCase().startsWith('CREA') ? professional.crea : `CREA: ${professional.crea}`);
      }
      if (professional?.cft) {
        registrationParts.push(String(professional.cft).toUpperCase().startsWith('CFT') ? professional.cft : `CFT: ${professional.cft}`);
      }

      currentRev.snapshotData.approval = {
        signatureImage: signatureImage && signatureImage.startsWith('data:image') ? signatureImage : null,
        plhName: professional?.name || user?.name || 'PLH',
        functionTitle: professional?.functionTitle || 'Engenheiro PLH',
        registration: registrationParts.join(' | '),
        approvedAt: approvedAt.toISOString(),
        hashShort: hash ? hash.substring(0, 12) : ''
      };
      await this.minutaService.minutaRepo.saveRevision(context, currentRev);
    }

    if (this.audit) {
      await this.audit.record(context, {
        action: AuditAction.MinutaPlhApproved,
        entityType: 'MINUTA',
        entityId: minuta.id,
        previousState: previousState as any,
        newState: minuta as any,
        metadata: {
          trackingNumber: minuta.trackingNumber,
          revision: minuta.currentRevision,
          approvedBy: context.userId,
          approvedByProfessionalId: user?.professionalId ?? null,
          signatureHash: hash
        }
      });
    }

    return minuta;
  }

  async submitMinutaToEngineering(
    context: RequestContext,
    minutaId: string
  ): Promise<Minuta> {
    if (!this.minutaService) throw new AppError(ErrorCode.NotFound, 'MinutaService not available');

    const minuta = await this.minutaService.getMinuta(context, minutaId);
    if (!minuta) throw new AppError(ErrorCode.NotFound, 'Minuta not found');

    if (minuta.status !== 'EMITIDA' && minuta.status !== 'ATUALIZAÇÃO PENDENTE') {
      throw new AppError(ErrorCode.Conflict, `Minuta não pode ser enviada para Engenharia no status atual: ${minuta.status}`);
    }

    minuta.status = 'AGUARDANDO ANÁLISE';
    minuta.updatedAt = new Date();
    await this.minutaService.minutaRepo.save(context, minuta);

    return minuta;
  }
}
